import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, CheckCircle2, Info, Lock, Loader2,
  ShieldCheck, ExternalLink, Phone, Sparkles,
} from "lucide-react";
import { submitSimulatorLead, requestSimulatorCallback } from "@/lib/api";
import AccompagnementBadge from "@/components/AccompagnementBadge";

const SIMULATOR_URL =
  "https://mesaides.france-renov.gouv.fr/aides/ma-prime-renov/Pompe%20%C3%A0%20chaleur%20air%2Feau";

const initialForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  address: "",
  postal_code: "",
  city: "",
  housing_type: "",
  ownership_status: "",
  residence: "",
  construction_year: "",
  surface_m2: "",
  household_size: "",
  fiscal_income: "",
  current_heating_type: "",
  current_energy: "",
  replaced_boiler_type: "",
  project: "pac_air_eau",
  gdpr_consent: false,
};

export default function SimulateurPac() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // {id, email, first_name}
  const [callbackSent, setCallbackSent] = useState(false);
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [studyConfirmed, setStudyConfirmed] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isValid = () => {
    const emailOk = /^\S+@\S+\.\S+$/.test(form.email);
    const phoneOk = form.phone.replace(/\D/g, "").length >= 8;
    const zipOk = /^\d{5}$/.test(form.postal_code);
    const yearOk = Number(form.construction_year) >= 1800 && Number(form.construction_year) <= new Date().getFullYear();
    return (
      form.first_name.trim().length >= 1 &&
      form.last_name.trim().length >= 1 &&
      emailOk && phoneOk && zipOk && yearOk &&
      form.address.trim().length >= 5 &&
      form.city.trim().length >= 1 &&
      form.housing_type && form.ownership_status && form.residence &&
      Number(form.surface_m2) >= 10 &&
      Number(form.household_size) >= 1 &&
      Number(form.fiscal_income) >= 0 &&
      form.current_heating_type.trim().length >= 1 &&
      form.current_energy &&
      form.gdpr_consent === true
    );
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!isValid()) {
      setError("Merci de renseigner tous les champs obligatoires et d'accepter le consentement RGPD.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        construction_year: Number(form.construction_year),
        surface_m2: Number(form.surface_m2),
        household_size: Number(form.household_size),
        fiscal_income: Number(form.fiscal_income),
      };
      const res = await submitSimulatorLead(payload);
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const onCallback = async () => {
    if (!result?.id || callbackSent) return;
    setCallbackLoading(true);
    try {
      await requestSimulatorCallback(result.id);
      setCallbackSent(true);
    } catch {
      setError("Impossible d'envoyer la demande de rappel. Réessayez.");
    } finally {
      setCallbackLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0F172A] text-white" data-testid="simulateur-pac-page">
      {result ? (
        <ResultScreen
          result={result}
          onCallback={onCallback}
          callbackSent={callbackSent}
          callbackLoading={callbackLoading}
          studyConfirmed={studyConfirmed}
          setStudyConfirmed={setStudyConfirmed}
          error={error}
        />
      ) : (
        <FormScreen form={form} set={set} submit={submit} submitting={submitting} error={error} />
      )}
    </main>
  );
}

/* ================= STEP 1 — FORM ================= */

function FormScreen({ form, set, submit, submitting, error }) {
  return (
    <div className="pt-28 pb-24 px-5 sm:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/25 bg-[#10B981]/[0.06] px-3 py-1.5 mb-5">
            <Sparkles className="h-3.5 w-3.5 text-[#10B981]" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] font-bold">
              Étude d&apos;éligibilité gratuite
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Vérifiez vos aides pour votre pompe à chaleur
          </h1>
          <p className="mt-5 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Renseignez votre situation en 2 minutes. Nous vous redirigeons ensuite vers le
            <strong className="text-white"> simulateur officiel France Rénov&apos;</strong> puis un conseiller
            ECONATION-CEE vérifie votre dossier.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-white/60 text-xs">
            <Row><ShieldCheck className="h-3.5 w-3.5 text-[#10B981]" /> Données 100% confidentielles</Row>
            <Row><Lock className="h-3.5 w-3.5 text-[#10B981]" /> Conforme RGPD</Row>
            <Row><CheckCircle2 className="h-3.5 w-3.5 text-[#10B981]" /> Sans engagement</Row>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-8">
          {/* Identité */}
          <Section title="1. Vos coordonnées">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input testid="sim-first_name" label="Prénom *" value={form.first_name} onChange={(v) => set("first_name", v)} />
              <Input testid="sim-last_name" label="Nom *" value={form.last_name} onChange={(v) => set("last_name", v)} />
              <Input testid="sim-phone" label="Téléphone *" type="tel" value={form.phone} onChange={(v) => set("phone", v)} placeholder="06 12 34 56 78" />
              <Input testid="sim-email" label="Adresse e-mail *" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="nom@exemple.fr" />
            </div>
          </Section>

          {/* Adresse */}
          <Section title="2. Adresse du logement">
            <div className="grid grid-cols-1 gap-3">
              <Input testid="sim-address" label="Numéro et rue *" value={form.address} onChange={(v) => set("address", v)} placeholder="12 rue de la République" />
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Input testid="sim-postal_code" label="Code postal *" value={form.postal_code} onChange={(v) => set("postal_code", v.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" maxLength={5} />
                </div>
                <div className="col-span-2">
                  <Input testid="sim-city" label="Ville *" value={form.city} onChange={(v) => set("city", v)} />
                </div>
              </div>
            </div>
          </Section>

          {/* Logement */}
          <Section title="3. Votre logement">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                testid="sim-housing_type" label="Type de logement *" value={form.housing_type}
                onChange={(v) => set("housing_type", v)}
                options={[{ v: "maison", l: "Maison" }, { v: "appartement", l: "Appartement" }]}
              />
              <Select
                testid="sim-ownership_status" label="Statut *" value={form.ownership_status}
                onChange={(v) => set("ownership_status", v)}
                options={[
                  { v: "proprietaire_occupant", l: "Propriétaire occupant" },
                  { v: "proprietaire_bailleur", l: "Propriétaire bailleur" },
                  { v: "locataire", l: "Locataire" },
                ]}
              />
              <Select
                testid="sim-residence" label="Résidence *" value={form.residence}
                onChange={(v) => set("residence", v)}
                options={[{ v: "principale", l: "Principale" }, { v: "secondaire", l: "Secondaire" }]}
              />
              <Input testid="sim-construction_year" label="Année de construction *" value={form.construction_year} onChange={(v) => set("construction_year", v.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="Ex : 1985" />
              <Input testid="sim-surface_m2" label="Surface chauffée (m²) *" value={form.surface_m2} onChange={(v) => set("surface_m2", v.replace(/\D/g, ""))} inputMode="numeric" placeholder="Ex : 110" />
              <Input testid="sim-household_size" label="Personnes dans le foyer *" value={form.household_size} onChange={(v) => set("household_size", v.replace(/\D/g, ""))} inputMode="numeric" placeholder="Ex : 4" />
              <div className="sm:col-span-2">
                <Input testid="sim-fiscal_income" label="Revenu fiscal de référence (€) *" value={form.fiscal_income} onChange={(v) => set("fiscal_income", v.replace(/\D/g, ""))} inputMode="numeric" placeholder="Ex : 42 000" hint="Indiqué sur votre dernier avis d'imposition." />
              </div>
            </div>
          </Section>

          {/* Chauffage */}
          <Section title="4. Votre chauffage actuel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input testid="sim-current_heating_type" label="Type de chauffage actuel *" value={form.current_heating_type} onChange={(v) => set("current_heating_type", v)} placeholder="Chaudière, radiateurs électriques…" />
              <Select
                testid="sim-current_energy" label="Énergie actuelle *" value={form.current_energy}
                onChange={(v) => set("current_energy", v)}
                options={[
                  { v: "gaz", l: "Gaz" }, { v: "fioul", l: "Fioul" },
                  { v: "electricite", l: "Électricité" }, { v: "bois", l: "Bois" },
                  { v: "autre", l: "Autre" },
                ]}
              />
              <div className="sm:col-span-2">
                <Input testid="sim-replaced_boiler_type" label="Type de chaudière remplacée (si applicable)" value={form.replaced_boiler_type} onChange={(v) => set("replaced_boiler_type", v)} placeholder="Ex : chaudière fioul basse température" />
              </div>
            </div>
          </Section>

          {/* Projet */}
          <Section title="5. Votre projet">
            <div className="rounded-2xl border-2 border-[#10B981] bg-[#10B981]/[0.08] p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#10B981]/20 text-[#10B981] grid place-items-center flex-shrink-0">
                <Check className="h-5 w-5" strokeWidth={3} />
              </div>
              <div>
                <div className="text-white font-semibold text-base">Pompe à chaleur air/eau</div>
                <div className="text-white/60 text-xs mt-0.5">Installation d&apos;une PAC pour chauffage + eau chaude sanitaire.</div>
              </div>
            </div>
          </Section>

          {/* GDPR */}
          <Section title="6. Consentement RGPD">
            <label className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 cursor-pointer hover:border-white/20 transition">
              <input
                type="checkbox"
                data-testid="sim-gdpr_consent"
                checked={form.gdpr_consent}
                onChange={(e) => set("gdpr_consent", e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-white/30 bg-white/10 text-[#10B981] focus:ring-[#10B981] flex-shrink-0"
              />
              <span className="text-white/80 text-sm leading-relaxed">
                J&apos;accepte que mes informations soient utilisées par <strong className="text-white">ECONATION-CEE</strong> afin
                d&apos;étudier mon éligibilité aux aides à la rénovation énergétique et de me recontacter concernant mon projet.
                Consultez notre <Link to="/confidentialite" className="text-[#10B981] underline hover:text-[#34D399]">politique de confidentialité</Link>.
              </span>
            </label>
          </Section>

          {error && (
            <div data-testid="sim-error" className="rounded-xl border border-[#F87171]/40 bg-[#F87171]/[0.06] px-4 py-3 text-[#FCA5A5] text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="sim-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-[#0055A4] hover:bg-[#003D7A] disabled:opacity-60 px-8 py-4 text-white text-sm font-semibold tracking-wide transition shadow-[0_10px_30px_-10px_rgba(0,85,164,0.6)] hover:-translate-y-0.5"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Enregistrement...</>
              ) : (
                <>Vérifier mes aides <ArrowRight className="h-4 w-4" /></>
              )}
            </button>
          </div>

          <div className="text-center text-white/40 text-[11px] leading-relaxed">
            Estimation indicative — aucune éligibilité définitive ne peut être garantie avant étude complète par un conseiller.
          </div>
        </form>

        {/* Accompagnement officiel */}
        <div className="mt-12">
          <AccompagnementBadge theme="dark" />
        </div>
      </div>
    </div>
  );
}

/* ================= STEP 2 — SIMULATOR + CTA ================= */

function ResultScreen({ result, onCallback, callbackSent, callbackLoading, studyConfirmed, setStudyConfirmed, error }) {
  return (
    <div className="pt-28 pb-24 px-5 sm:px-8" data-testid="sim-result-screen">
      <div className="max-w-5xl mx-auto">
        {/* Success confirmation */}
        <div className="text-center mb-12">
          <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] grid place-items-center shadow-[0_20px_60px_-10px_rgba(16,185,129,0.4)]">
            <Check className="h-8 w-8 text-white" strokeWidth={3} />
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#10B981]/25 bg-[#10B981]/[0.06] px-3 py-1.5 mb-5">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#10B981] font-bold">Demande enregistrée</span>
          </div>
          {result.dossier_number && (
            <div className="mx-auto mb-6 max-w-md rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4" data-testid="sim-dossier-number">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 font-semibold mb-1">Votre numéro de dossier</div>
              <div className="font-display text-2xl sm:text-3xl font-bold tracking-wider text-white">{result.dossier_number}</div>
              <div className="text-white/50 text-[11px] mt-1">À conserver pour toute correspondance</div>
            </div>
          )}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] mb-5">
            Découvrez les aides disponibles pour votre pompe à chaleur
          </h1>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
            Selon votre situation, votre projet de pompe à chaleur peut être financé par plusieurs dispositifs,
            notamment <strong className="text-white">MaPrimeRénov&apos;</strong>, les <strong className="text-white">Certificats
            d&apos;économies d&apos;énergie — CEE</strong> et la <strong className="text-white">prime Coup de pouce Chauffage</strong>.
          </p>
          <p className="text-white/60 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto mt-4">
            Le montant définitif dépend notamment de vos revenus, de la composition de votre foyer, de votre logement,
            de votre chauffage actuel et des caractéristiques techniques de l&apos;installation.
          </p>
          <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto mt-4">
            Utilisez le <strong className="text-white">simulateur officiel</strong> ci-dessous pour obtenir une première estimation.
            ECONATION-CEE vérifiera ensuite votre dossier et vous accompagnera dans les démarches.
          </p>
        </div>

        {/* Iframe */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-3 sm:p-5 mb-10 shadow-2xl" data-testid="sim-iframe-wrapper">
          <div className="flex items-center justify-between mb-3 px-3">
            <div className="flex items-center gap-2 text-white/70 text-xs">
              <div className="h-2 w-2 rounded-full bg-[#10B981]" />
              <span className="uppercase tracking-[0.18em] font-semibold">Simulateur officiel France Rénov&apos;</span>
            </div>
            <a
              href={SIMULATOR_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-xs font-medium transition"
              data-testid="sim-iframe-external"
            >
              Ouvrir en plein écran <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          <div className="relative w-full overflow-hidden rounded-2xl bg-white" style={{ paddingBottom: "min(140%, 1000px)" }}>
            <iframe
              title="Simulateur France Rénov' — Pompe à chaleur air/eau"
              src={SIMULATOR_URL}
              className="absolute inset-0 w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              data-testid="sim-iframe"
            />
          </div>
          <div className="mt-3 px-3 flex items-start gap-2 text-white/50 text-[11px] leading-relaxed">
            <Info className="h-3.5 w-3.5 text-[#10B981] mt-0.5 flex-shrink-0" />
            <span>
              Simulateur fourni par France Rénov&apos; (service public). Les résultats affichés sont une <strong className="text-white/70">estimation indicative</strong> et ne constituent pas une confirmation d&apos;éligibilité.
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <button
            data-testid="sim-cta-study"
            onClick={() => setStudyConfirmed(true)}
            disabled={studyConfirmed}
            className="group rounded-2xl bg-[#0055A4] hover:bg-[#003D7A] disabled:opacity-90 disabled:cursor-default px-6 py-5 text-white font-semibold tracking-wide transition shadow-[0_10px_30px_-10px_rgba(0,85,164,0.55)] hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            {studyConfirmed ? (
              <><Check className="h-5 w-5" strokeWidth={3} /> Demande confirmée</>
            ) : (
              <>Recevoir mon étude personnalisée ECONATION <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" /></>
            )}
          </button>
          <button
            data-testid="sim-cta-callback"
            onClick={onCallback}
            disabled={callbackSent || callbackLoading}
            className="rounded-2xl border-2 border-white/15 hover:border-white/30 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-90 disabled:cursor-default px-6 py-5 text-white font-semibold tracking-wide transition flex items-center justify-center gap-2"
          >
            {callbackLoading ? (
              <><Loader2 className="h-5 w-5 animate-spin" /> Envoi...</>
            ) : callbackSent ? (
              <><Check className="h-5 w-5 text-[#10B981]" strokeWidth={3} /> Rappel demandé</>
            ) : (
              <><Phone className="h-4 w-4" /> Être rappelé par un conseiller</>
            )}
          </button>
        </div>

        {studyConfirmed && (
          <div data-testid="sim-study-confirm" className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/[0.08] p-5 sm:p-6 text-center mb-6">
            <div className="text-white font-semibold text-base mb-1">Votre demande a bien été enregistrée</div>
            <div className="text-white/70 text-sm">
              Un conseiller <strong className="text-white">ECONATION-CEE</strong> va vérifier votre éligibilité et vous recontacter.
            </div>
          </div>
        )}

        {callbackSent && (
          <div data-testid="sim-callback-confirm" className="rounded-2xl border border-[#0055A4]/30 bg-[#0055A4]/[0.08] p-5 sm:p-6 text-center mb-6">
            <div className="text-white font-semibold text-base mb-1">📞 Votre demande de rappel est confirmée</div>
            <div className="text-white/70 text-sm">
              Un conseiller vous rappellera très prochainement au numéro renseigné.
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-[#F87171]/40 bg-[#F87171]/[0.06] px-4 py-3 text-[#FCA5A5] text-sm mb-6">
            {error}
          </div>
        )}

        {/* Accompagnement officiel */}
        <div className="mb-8">
          <AccompagnementBadge theme="dark" />
        </div>

        {/* RGPD footer */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-white/60 text-xs leading-relaxed">
          <div className="flex items-start gap-2 mb-2">
            <Lock className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-white font-semibold text-sm mb-1">Protection de vos données</div>
              Vos informations sont traitées conformément au RGPD, dans le seul but d&apos;étudier votre projet
              et de vous accompagner dans les démarches. Consultez notre <Link to="/confidentialite" className="text-[#10B981] underline hover:text-[#34D399]">politique de confidentialité</Link>.
              Un email de confirmation vient de vous être envoyé à <strong className="text-white/80">{result.email}</strong>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= UI HELPERS ================= */

function Section({ title, children }) {
  return (
    <div>
      <div className="text-white/85 font-semibold text-sm mb-3 uppercase tracking-[0.14em]">{title}</div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="inline-flex items-center gap-1.5">{children}</div>;
}

function Input({ testid, label, value, onChange, type = "text", placeholder = "", inputMode, maxLength, hint }) {
  return (
    <div>
      <label className="block text-white/70 text-xs font-medium mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-white text-base placeholder:text-white/25 outline-none transition"
      />
      {hint && <div className="text-white/40 text-[11px] mt-1.5">{hint}</div>}
    </div>
  );
}

function Select({ testid, label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-white/70 text-xs font-medium mb-1.5">{label}</label>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none bg-white/[0.03] border border-white/10 focus:border-[#10B981] rounded-xl px-4 py-3 text-white text-base outline-none transition"
        >
          <option value="" className="bg-[#0F172A]">— Sélectionner —</option>
          {options.map((o) => (
            <option key={o.v} value={o.v} className="bg-[#0F172A]">{o.l}</option>
          ))}
        </select>
        <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40">
          <ArrowLeft className="h-3.5 w-3.5 -rotate-90" />
        </div>
      </div>
    </div>
  );
}
